import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { startCase } from "lodash";

const VpfDisplay: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const vpfData = location.state?.vpfData;

  const user: any = localStorage.getItem("user");
  const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
  const currentVpfAmount = userJson?.details?.vPFAmount || '0.00';

  if (!vpfData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">No VPF Data found. Please return to the list and select a request.</div>
          <button className="btn btn-primary" onClick={() => navigate(all_routes.vpfModule.list)}>Back to List</button>
        </div>
      </div>
    );
  }

  // Consistent Date Format: DD-MM-YYYY
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "-";
    }
  };



  const getStatusBadgeClass = (status: string) => {
    if (!status) return "badge-secondary";
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes("pending") || lowerStatus.includes("to be approved")) {
      return "badge-warning";
    } else if (lowerStatus.includes("approved") || lowerStatus === "a") {
      return "badge-success";
    } else if (lowerStatus.includes("reject") || lowerStatus === "r") {
      return "badge-danger";
    } else if (lowerStatus.includes("progress") || lowerStatus.includes("insapprocess")) {
      return "badge-info";
    } else {
      return "badge-secondary";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">VPF Request Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.vpfModule.list}>VPF Contribution</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <button className="btn btn-light" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-2"></i>Back
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0 d-flex align-items-center">
                  <i className="ti ti-file-description text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Reference Information
                </h5>
                <span className={`badge ${getStatusBadgeClass(vpfData.RequestStatus || vpfData.Status)}`} style={{ fontSize: "0.8rem", padding: "0.4rem 0.6rem" }}>
                  {(vpfData.RequestStatus || vpfData.Status) === 'T' ? 'To Be Approved' : startCase(vpfData.RequestStatus || vpfData.Status || "Pending")}
                </span>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="text-muted fw-semibold mb-1">Reference Number</label>
                    <div className="text-dark fw-medium">{vpfData.ReferenceNumber || "-"}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted fw-semibold mb-1">Creation Date</label>
                    <div className="text-dark fw-medium">{formatDate(vpfData.RequestCreationDate)}</div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="text-muted fw-semibold mb-1">Current VPF Amount (₹)</label>
                    <div className="text-dark fw-medium">{currentVpfAmount}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0 d-flex align-items-center">
                  <i className="ti ti-wallet text-danger me-2" style={{ fontSize: "1.5rem" }}></i>VPF Details
                </h5>
              </div>
              <div className="card-body">
                <div className="alert alert-success d-flex align-items-center mb-4">
                  <i className="ti ti-coin me-3" style={{ fontSize: "1.5rem" }}></i>
                  <div>
                    <div className="fw-semibold">Requested VPF Amount</div>
                    <div className="fw-bold text-dark mt-1" style={{ fontSize: "1.25rem" }}>
                      ₹ {parseFloat(vpfData.VpfAmount || "0").toFixed(2)}
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="text-muted fw-semibold mb-1">Deduction Month</label>
                    <div className="text-dark fw-medium">{vpfData.DeductionFromTheMonth || "-"}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted fw-semibold mb-1">Deduction Year</label>
                    <div className="text-dark fw-medium">{vpfData.DeductionFromTheYear || "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0 d-flex align-items-center">
                  <i className="ti ti-user text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Approver Details
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Approver Name</label>
                    <div className="text-dark fw-medium">{vpfData.ApproverName || (vpfData.approver ? `${vpfData.approver.firstname || ''} ${vpfData.approver.lsastname || ''}`.trim() : "-")}</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Approver ID</label>
                    <div className="text-dark fw-medium">{vpfData.ApproverUserID || "-"}</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Designation</label>
                    <div className="text-dark fw-medium">{vpfData.approver?.sap_department_designation_wings?.designation_name || "-"}</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Department</label>
                    <div className="text-dark fw-medium">{vpfData.approver?.sap_department_designation_wings?.department_name || "-"}</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Email</label>
                    <div className="text-dark fw-medium">{vpfData.approver?.email || "-"}</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="text-muted fw-semibold mb-1">Phone</label>
                    <div className="text-dark fw-medium">{vpfData.approver?.phone || "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {vpfData.Remarks && (
            <div className="col-lg-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <i className="ti ti-message text-danger me-2" style={{ fontSize: "1.5rem" }}></i>Remarks
                  </h5>
                </div>
                <div className="card-body">
                  <p className="fst-italic">{vpfData.Remarks}</p>
                </div>
              </div>
            </div>
          )}

          <div className="col-lg-12">
            <div className="alert alert-info d-flex align-items-center">
              <i className="ti ti-info-circle fs-4 me-2"></i>
              <div>Note: This is a read-only view of your VPF Request. To make changes, please create a new request.</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default VpfDisplay;
